import { useState, useEffect } from 'react';

import { useQueryClient } from 'react-query';

import FactDetail from './FactDetail';
import FactTitle from './FactTitle';
import { useSetKeyForFacts } from '../../../api/mutations/useSetKeyForFacts';
import { TrackingEvents } from '@/analytics/events';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useDeleteFact } from '@/api/mutations/useDeleteFact';
import useUpdateFact from '@/api/mutations/useUpdateFact';
import { QueryEntity } from '@/api/types';
import Accordion from '@/components/molecules/Accordion';
import { ConfirmationModal } from '@/components/molecules/Modals/Confirmation';
import { useUserContext } from '@/Contexts/User';
import { Fact, Relevance } from '@/types';

// Interfaces
interface FactDropdownProps {
  fact: Fact;
  isKey?: boolean;
  compressedVersion?: boolean;
  reference?: number;
  hideRelevanceIndicator?: boolean;
  onViewDocument?: (docId: string, factId: string, pageNumber: number) => void;
}

const FactDropdown = ({
  fact,
  isKey,
  reference,
  hideRelevanceIndicator,
  compressedVersion = false,
  onViewDocument,
}: FactDropdownProps) => {
  const [open, setOpen] = useState(false);
  const [relevance, setRelevance] = useState(fact.relevance);
  const queryClient = useQueryClient();
  const { user } = useUserContext();

  const isLabellerOrg = user?.organisation?.isLabellingOrg ?? false;

  useEffect(() => {
    setRelevance(fact.relevance);
  }, [fact.relevance]);

  // TODO: revisit this duplication, currently we have 4 to manage the loading states for each field
  const { mutate: updateFactRelevance, isLoading: isUpdatingRelevance } = useUpdateFact(fact.caseId);
  const { mutate: updateFactSignificance, isLoading: isUpdatingSignificance } = useUpdateFact(fact.caseId);
  const { mutate: updateFactDescription, isLoading: isUpdatingDescription } = useUpdateFact(fact.caseId);
  const { mutate: updateFactDateText, isLoading: isUpdatingDateText } = useUpdateFact(fact.caseId);

  const onSuccess = () => {
    const { caseId } = fact;
    if (fact.caseId) {
      queryClient.invalidateQueries([QueryEntity.CaseFacts, { caseId }]);
      queryClient.invalidateQueries([QueryEntity.KeyFacts, { caseId }]);
    }
  };

  const handleUpdateRelevance = (newRelevance: Relevance) => {
    setRelevance(newRelevance);
    updateFactRelevance(
      { field: 'relevance', value: newRelevance, factId: fact.id },
      {
        onSuccess: () => {
          trackCustomEvent(TrackingEvents.UpdateFactRelevance);
          onSuccess();
        },
      },
    );
  };

  const handleUpdateSignificance = (newSignificance: string) => {
    updateFactSignificance(
      { field: 'significance', value: newSignificance, factId: fact.id },
      {
        onSuccess: () => {
          trackCustomEvent(TrackingEvents.UpdateFactContent);
          onSuccess();
        },
      },
    );
  };

  const handleUpdateDateText = (newDateText: string) => {
    updateFactDateText(
      { field: 'factDateText', value: newDateText, factId: fact.id },
      {
        onSuccess: () => {
          trackCustomEvent(TrackingEvents.UpdateFactDate);
          onSuccess();
        },
      },
    );
  };

  const handleUpdateDescription = (newDescription: string) => {
    updateFactDescription(
      { field: 'description', value: newDescription, factId: fact.id },
      {
        onSuccess: () => {
          trackCustomEvent(TrackingEvents.UpdateFactContent);
          onSuccess();
        },
      },
    );
  };

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const { mutate: deleteFact, isLoading: isDeletingFact } = useDeleteFact({
    factId: fact.id,
    caseId: fact.caseId,
  });

  const handleOpenDeleteModal = () => {
    setIsDeleteModalOpen(true);
  };

  const handleDeleteFact = () => {
    deleteFact(undefined, {
      onSuccess: () => {
        trackCustomEvent(TrackingEvents.DeleteFact);
        setIsDeleteModalOpen(false);
      },
    });
  };

  const factDeleteDescription = (
    <div>
      <p className="text-sm">
        Are you sure you want to archive this fact? This fact will still be able to be referenced elsewhere, but won't
        appear in the list of facts.
      </p>

      <p className="pt-3 text-sm text-gray-500">
        Currently you cannot view or un-archive archived facts. This will be added in a future update.
      </p>
    </div>
  );

  return (
    <>
      <Accordion
        title={
          <FactTitle
            fact={fact}
            isKey={isKey}
            factText={fact.description}
            dateText={fact.factDateText}
            relevance={relevance}
            open={open}
            reference={reference}
            compressedVersion={compressedVersion}
            hideRelevanceIndicator={hideRelevanceIndicator}
            onViewDocument={onViewDocument}
            updateFactDateText={handleUpdateDateText}
            updateFactDescription={handleUpdateDescription}
            isUpdatingDateText={isUpdatingDateText}
            isUpdatingDescription={isUpdatingDescription}
            isLabellerOrg={isLabellerOrg}
          />
        }
        content={
          <FactDetail
            fact={fact}
            significanceText={fact.significance}
            relevanceText={relevance}
            updateRelevance={handleUpdateRelevance}
            updateSignificance={handleUpdateSignificance}
            isUpdatingSignificance={isUpdatingSignificance}
            isUpdatingRelevance={isUpdatingRelevance}
            compressedVersion={compressedVersion}
            onViewDocument={onViewDocument}
            handleOpenDeleteModal={handleOpenDeleteModal}
          />
        }
        className="relative p-0"
        outerOpen={setOpen}
      />

      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        handleClose={() => setIsDeleteModalOpen(false)}
        title="Archive Fact"
        description={factDeleteDescription}
        onConfirm={handleDeleteFact}
        isLoading={isDeletingFact}
        confirmButtonText="Archive Fact"
        confirmButtonVariant="destructive"
      />
    </>
  );
};

export default FactDropdown;
